import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

const services = [
  {
    title: 'Consulting',
    heading: 'Infor OS Consulting',
    description:
      'We provide strategic consulting to help you leverage Infor OS as the foundation for your digital transformation journey. Our team develops comprehensive digital transformation strategies, creates solution architectures and planning, conducts business process assessments, and builds platform roadmaps aligned with your business goals. We work closely with your leadership to define clear paths that ensure your Infor OS investment delivers maximum value while enabling innovation and future growth.',
    points: [
      'Digital transformation strategy',
      'Solution architecture & planning',
      'Business process assessment',
    ],
  },
  {
    title: 'Implementation',
    heading: 'Infor OS Implementation',
    description:
      'We manage end-to-end Infor OS deployments from project kickoff to go-live and beyond. Our implementation services cover platform configuration, user and security setup, environment provisioning, and comprehensive go-live support. We ensure that every aspect of your Infor OS platform—from infrastructure to user experience—is properly configured to support your unique business requirements. Our team works alongside your project team to establish realistic timelines, manage risks proactively, and deliver a robust platform that drives digital innovation.',
    points: [
      'End-to-end Infor OS deployment',
      'Platform configuration & user setup',
      'Environment provisioning & go-live support',
    ],
  },
  {
    title: 'Enterprise Integration',
    heading: 'Enterprise Integration Services',
    description:
      'We connect Infor OS with surrounding applications and systems to create a unified digital ecosystem. Our integration services include Infor ION integration, ERP integration (Infor LN, M3, CSI), CRM integration, HR and Finance system integration, third-party application integration, and API and web service development. We design robust integration architectures that ensure reliable data flow, handle exceptions effectively, and provide real-time synchronization where needed, creating a seamless digital experience across your enterprise.',
    points: [
      'Infor ION & ERP integration',
      'CRM, HR & Finance system integration',
      'API and web service development',
    ],
  },
  {
    title: 'Workflow Automation',
    heading: 'Workflow Automation',
    description:
      'We help you automate business processes with Infor OS workflow capabilities to improve efficiency and reduce manual effort. Our workflow automation services include business workflow design, approval workflows, event-driven automation, document routing, and business process orchestration. We design and implement automated workflows that streamline operations, enforce business rules, and provide real-time visibility into process status. Our approach reduces cycle times, eliminates bottlenecks, and ensures consistent execution of business processes.',
    points: [
      'Business workflow & approval workflows',
      'Event-driven automation',
      'Document routing & process orchestration',
    ],
  },
  {
    title: 'Infor ION Services',
    heading: 'Infor ION Services',
    description:
      'We provide comprehensive Infor ION services to enable seamless integration and data exchange across your enterprise. Our services include ION Connect configuration, Business Object Document (BOD) integration, ION API development, event management, and message monitoring and troubleshooting. We ensure reliable, secure, and efficient data flow between your Infor OS platform and connected systems, enabling real-time visibility and process synchronization across your enterprise ecosystem.',
    points: [
      'ION Connect & BOD integration',
      'ION API development',
      'Event management & message monitoring',
    ],
  },
  {
    title: 'Ming.le & Collaboration',
    heading: 'Infor Ming.le & Collaboration',
    description:
      'We implement and configure Infor Ming.le to enable seamless collaboration and personalized user experiences. Our Ming.le services include digital workspace setup, role-based homepages, social collaboration, alerts and notifications, and personalized user experiences. We create intuitive, engaging digital workspaces that improve user adoption, enhance team collaboration, and provide employees with the information they need to make better decisions faster.',
    points: [
      'Digital workspace & role-based homepages',
      'Social collaboration',
      'Alerts, notifications & personalized experiences',
    ],
  },
  {
    title: 'Cloud Migration & Upgrades',
    heading: 'Cloud Migration & Platform Upgrades',
    description:
      'We help organizations modernize their Infor OS environment with structured cloud migration, platform upgrades, and optimization. Our services include cloud migration, platform upgrades, environment modernization, performance optimization, and release management. We conduct thorough pre-migration assessments, develop comprehensive migration strategies, and provide post-migration support to ensure a smooth transition. Our expertise ensures you unlock the full benefits of cloud capabilities and new features while maintaining platform stability and performance.',
    points: [
      'Cloud migration & platform upgrades',
      'Environment modernization',
      'Performance optimization & release management',
    ],
  },
  {
    title: 'Support & Managed Services',
    heading: 'Support & Managed Services',
    description:
      'We provide comprehensive support and managed services for Infor OS to ensure platform stability and continuous improvement. Our support services include 24/7 application support, incident management, performance monitoring, preventive maintenance, SLA-based support, and continuous improvements. Our support model includes tiered escalation, proactive monitoring, and regular platform health checks to keep your Infor OS environment running smoothly. We work collaboratively with your internal teams to build knowledge and reduce dependency over time.',
    points: [
      '24/7 application support',
      'Incident management & performance monitoring',
      'Preventive maintenance & continuous improvements',
    ],
  },
];

const InforOSOverview = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeService = services[activeIndex];

  return (
    <section
      id="inforos-overview"
      className="px-4 py-16 sm:px-6 lg:px-20 lg:py-20"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="mx-auto max-w-8xl">
        <div className="text-center">
          <h2
            className="text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl"
            style={{ color: '#1A2634' }}
          >
            Our Infor OS Consulting Services
          </h2>
          <p
            className="mt-4 text-base text-center sm:text-lg"
            style={{ color: '#000000', fontWeight: 400 }}
          >
            Expert solutions to maximize your Infor OS investment.
          </p>
        </div>

        <div className="mt-12">
          <h3
            className="text-3xl font-bold sm:text-4xl"
            style={{ color: '#1A2634' }}
          >
            Infor OS Expertise
          </h3>

          <div className="mt-8 grid gap-8 lg:grid-cols-[0.8fr_1.45fr] lg:items-start">
            <div
              className="space-y-1 border-l-4 py-2"
              style={{ borderColor: '#F0F0F0' }}
            >
              {services.map((service, index) => {
                const isActive = index === activeIndex;

                return (
                  <button
                    key={service.title}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className="block w-full px-5 py-2.5 text-left text-sm font-bold transition duration-300 sm:text-base"
                    style={{
                      color: isActive ? '#0F172A' : '#1F2937',
                      borderLeft: isActive ? '4px solid #D81820' : '4px solid transparent',
                      transform: isActive ? 'translateX(-4px)' : 'translateX(0)',
                    }}
                  >
                    {service.title}
                  </button>
                );
              })}
            </div>

            <div
              className="rounded-lg border p-6 sm:p-7 lg:p-8"
              style={{
                backgroundColor: '#FFFFFF',
                borderColor: 'rgba(15, 23, 42, 0.06)',
                boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
              }}
            >
              <p
                className="inforos-overview-description w-full max-w-none md:text-md sm:text-lg"
                style={{
                  color: '#000000',
                  fontWeight: 400,
                  textAlign: 'justify',
                  textJustify: 'inter-word',
                  wordBreak: 'normal',
                  overflowWrap: 'normal',
                  hyphens: 'none',
                  whiteSpace: 'normal',
                }}
              >
                {activeService.description}
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {activeService.points.map((point) => (
                  <div
                    key={point}
                    className="flex gap-2.5 rounded-lg p-3"
                    style={{ backgroundColor: '#F8FAFC' }}
                  >
                    <CheckCircle2
                      className="mt-0.5 h-4 w-4 shrink-0"
                      style={{ color: '#D81820' }}
                    />
                    <p
                      className="text-xs font-medium leading-5 sm:text-sm"
                      style={{ color: '#1A2634', fontWeight: 500 }}
                    >
                      {point}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InforOSOverview;